// src/components/mitchRoom/revenueAnalytics/MRRWaterfallChart.js
import React, { useMemo } from 'react';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  Cell,
} from 'recharts';
import { Skeleton } from 'components/ui/skeleton';
import { cn } from 'lib/utils';
import { formatCurrency, formatPeriod } from 'utils/format';

const COLOR_POSITIVE = '#59A14F';
const COLOR_NEGATIVE = '#E15759';
const COLOR_LINE = '#1072CA';

const gbpWhole = (value) => formatCurrency(value, { whole: true });

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;

  const data = payload[0]?.payload;
  if (!data) return null;

  const netPositive = data.net_change >= 0;

  return (
    <div className="rounded-token-md border border-borderGrey bg-white p-3 text-token-base shadow-md">
      <p className="mb-2 font-semibold">{formatPeriod(label)}</p>
      <div className="flex flex-col gap-1">
        <div className="flex justify-between gap-4">
          <span className="text-muted-foreground">Starting MRR:</span>
          <span>{gbpWhole(data.starting_mrr)}</span>
        </div>
        <div className="flex justify-between gap-4 text-success">
          <span>+ New MRR:</span>
          <span>{gbpWhole(data.new_mrr)}</span>
        </div>
        <div className="flex justify-between gap-4 text-destructive">
          <span>- Churned MRR:</span>
          <span>{gbpWhole(data.churned_mrr)}</span>
        </div>
        <div
          className={cn(
            'flex justify-between gap-4 font-semibold',
            netPositive ? 'text-success' : 'text-destructive'
          )}
        >
          <span>Net MRR:</span>
          <span>
            {netPositive ? '+' : ''}
            {gbpWhole(data.net_change)}
          </span>
        </div>
        <div className="mt-1 flex justify-between gap-4 border-t border-borderGrey pt-1 font-semibold">
          <span>Ending MRR:</span>
          <span>{gbpWhole(data.ending_mrr)}</span>
        </div>
        {data.one_time_revenue > 0 && (
          <div className="flex justify-between gap-4">
            <span className="text-muted-foreground">One-time:</span>
            <span>{gbpWhole(data.one_time_revenue)}</span>
          </div>
        )}
        <div className="mt-1 flex justify-between gap-4 border-t border-borderGrey pt-1">
          <span className="text-muted-foreground">Subscribers:</span>
          <span>{data.ending_subscribers}</span>
        </div>
      </div>
    </div>
  );
};

export default function MRRWaterfallChart({ data, loading }) {
  // Transform data for the waterfall visualization
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];

    return data.map((item) => ({
      ...item,
      // For the waterfall, we show:
      // - Base (starting_mrr) as invisible base
      // - new_mrr as green positive bar
      // - churned_mrr as red negative bar (shown below the line)
      base: item.starting_mrr,
      new_positive: item.new_mrr,
      churned_negative: -item.churned_mrr, // Negative for display below baseline
      net_change: item.new_mrr - item.churned_mrr,
    }));
  }, [data]);

  // Calculate summary metrics
  const summary = useMemo(() => {
    if (!chartData || chartData.length === 0) {
      return { totalNew: 0, totalChurned: 0, netChange: 0, currentMRR: 0 };
    }

    const latest = chartData[chartData.length - 1];
    const totalNew = chartData.reduce((sum, d) => sum + d.new_mrr, 0);
    const totalChurned = chartData.reduce((sum, d) => sum + d.churned_mrr, 0);

    return {
      totalNew,
      totalChurned,
      netChange: totalNew - totalChurned,
      currentMRR: latest?.ending_mrr || 0,
    };
  }, [chartData]);

  if (loading && (!data || data.length === 0)) {
    return (
      <div className="rounded-token-large border border-borderGrey bg-white p-4">
        <h3 className="mb-3 text-token-xl font-semibold">MRR Waterfall</h3>
        <Skeleton className="h-[350px] w-full" />
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="rounded-token-large border border-borderGrey bg-white p-4">
        <h3 className="text-token-xl font-semibold">MRR Waterfall</h3>
        <div className="flex h-[350px] items-center justify-center">
          <p className="text-token-md text-muted-foreground">No MRR data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-token-large border border-borderGrey bg-white p-4">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-4">
        <h3 className="text-token-xl font-semibold">MRR Waterfall</h3>
        <div className="flex gap-6">
          <div className="text-right">
            <p className="text-token-sm text-muted-foreground">Current MRR</p>
            <p className="text-token-xl font-semibold">{gbpWhole(summary.currentMRR)}</p>
          </div>
          <div className="text-right">
            <p className="text-token-sm text-muted-foreground">Net Change</p>
            <p
              className={cn(
                'text-token-xl font-semibold',
                summary.netChange >= 0 ? 'text-success' : 'text-destructive'
              )}
            >
              {summary.netChange >= 0 ? '+' : ''}
              {gbpWhole(summary.netChange)}
            </p>
          </div>
        </div>
      </div>

      <div className="h-[400px] w-full min-w-0">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartData} margin={{ top: 20, right: 20, left: 10, bottom: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#DADADA" vertical={false} />
            <XAxis
              dataKey="period"
              tickFormatter={formatPeriod}
              tick={{ fontSize: 11 }}
              angle={-45}
              textAnchor="end"
              height={50}
            />
            <YAxis
              tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
              tick={{ fontSize: 12 }}
              width={50}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              verticalAlign="top"
              height={36}
              formatter={(value) => {
                const labels = {
                  net_change: 'Net MRR Change',
                  ending_mrr: 'Ending MRR',
                };
                return labels[value] || value;
              }}
            />

            {/* Net MRR Change - green if positive, red if negative */}
            <Bar dataKey="net_change" radius={[3, 3, 0, 0]} name="net_change" isAnimationActive={false}>
              {chartData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry.net_change >= 0 ? COLOR_POSITIVE : COLOR_NEGATIVE}
                />
              ))}
            </Bar>

            {/* Ending MRR line */}
            <Line
              type="monotone"
              dataKey="ending_mrr"
              stroke={COLOR_LINE}
              strokeWidth={3}
              dot={{ fill: COLOR_LINE, r: 4 }}
              name="ending_mrr"
              isAnimationActive={false}
            />

            {/* Reference line at zero */}
            <ReferenceLine y={0} stroke="#DADADA" />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
